import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@box-env/environment';
import { APIResponse, APIError, Address, NavigatorItem, SortingPerDay } from '@box-types';
import {
  filterEnabledItems,
  getPostalCodes,
  HomeSection,
  normalizeHomeSection,
  ObservableCacheDecorator,
  NavigatorSection
} from '@box/utils';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import defaultHomeSections from '@box-data/default-home-sections.json';
import { SentryService } from '@box-core/services/sentry.service';
import { GlobalStateService } from '@box-core/services/global-state.service';
import { v4 as uuidv4 } from 'uuid';

const SECTIONS_SESSION_EXPIRATION = 10 * 60 * 1000; // 10 minutes;
@Injectable({ providedIn: 'root' })
export class HomeSectionsService {
  private readonly BOX_API = environment.application.API_URL;
  private readonly homeSectionsSource = new BehaviorSubject<HomeSection[]>([]);
  public readonly homeSections$ = this.homeSectionsSource.asObservable();
  private readonly navigatorSectionSource = new BehaviorSubject<NavigatorSection>(null);
  public readonly navigatorSection$ = this.navigatorSectionSource.asObservable();

  constructor(
    private http: HttpClient,
    private sentryService: SentryService,
    private globalStateService: GlobalStateService
  ) {}

  @ObservableCacheDecorator({ expirationTimeInMs: SECTIONS_SESSION_EXPIRATION })
  public fetchHomeSections$(): Observable<HomeSection[]> {
    return this.http.get(`${this.BOX_API}/sections`).pipe(
      map((response: APIResponse<{ sections: HomeSection[] }>) => response.payload.sections),
      catchError((response: APIError) => {
        this.sentryService.captureException(response, {
          domain: 'sections',
          domainDetails: 'Loading defaults',
          severity: 'warning'
        });
        return of(defaultHomeSections as HomeSection[]);
      })
    );
  }

  @ObservableCacheDecorator({ expirationTimeInMs: SECTIONS_SESSION_EXPIRATION })
  public fetchHomeNavigator$(): Observable<NavigatorSection | null> {
    return this.http.get(`${this.BOX_API}/home`).pipe(
      map(
        (response: APIResponse<{ navigatorItems: NavigatorItem[] }>): NavigatorSection => ({
          navigatorItems: response.payload.navigatorItems
        })
      ),
      catchError((response: APIError) => {
        this.sentryService.captureException(response, {
          domain: 'navigator sections',
          domainDetails: 'Loading defaults',
          severity: 'warning'
        });
        return of(null);
      })
    );
  }

  public getHomeSections$(): Observable<HomeSection[]> {
    return this.fetchHomeSections$().pipe(
      tap((sections) => {
        this.setHomeSections(sections);
      })
    );
  }

  public initializeHomeSections(homeSections: HomeSection[]): HomeSection[] {
    const normalizedSections = homeSections.map((section) => normalizeHomeSection(section));
    const userSegments = this.globalStateService.getUser()?.segments ?? [];
    const availableCoupons = this.globalStateService.getAvailableCoupons();
    const eligibleGroupings = this.globalStateService.getEligibleGroupings();
    const userAddress = this.globalStateService.getAddress();
    const location = this.globalStateService.getLocation();
    const postalCodes = getPostalCodes(userAddress, location);
    return filterEnabledItems(normalizedSections, userSegments, availableCoupons, postalCodes, eligibleGroupings);
  }

  public setHomeSections(sections: HomeSection[]): void {
    this.homeSectionsSource.next(sections);
  }

  public getHomeSections(): HomeSection[] {
    return this.homeSectionsSource.getValue();
  }

  public getNavigatorSection$(): Observable<NavigatorSection | null> {
    return this.fetchHomeNavigator$().pipe(
      tap((section) => {
        this.setNavigatorSection(section);
      })
    );
  }

  public setNavigatorSection(section: NavigatorSection): void {
    this.navigatorSectionSource.next(section);
  }

  public getNavigatorSection(): NavigatorSection {
    return this.navigatorSectionSource.getValue();
  }

  public normalizeNavigatorSection(navigatorItems: NavigatorItem[], defaultNavigatorSection: HomeSection): HomeSection {
    if (!navigatorItems || navigatorItems.length === 0) return;
    return { ...defaultNavigatorSection, navigatorItems: navigatorItems };
  }
}
